/**
 * Shared RECURSIVE UNWRAP helper for the cloud-sync pull path.
 *
 * Extracted from UniversalSyncBridge.js on 2026-07-07 (FT-331 amend) so that
 * both UniversalSyncBridge.js and lib/outbox.js can use the SAME hoisted
 * implementation. Prior to this split, outbox.js carried its own inline copy
 * (lines 271-287 pre-amend) which shared the FT-324 ghost-doc-poisoning bug
 * class and was NOT fixed by the initial FT-331 patch — the "belt-and-braces"
 * guard was only wired into UniversalSyncBridge. Now both sync paths funnel
 * through this one guard.
 *
 * Cloud may store shapes like:
 *   [{id: "vessel_crew", data: {items: [{id: "vessel_crew", data: {items: [...]}}]}}]
 * We need to flatten to real items:
 *   [{id: "CRW-xxx", name: "John"}, ...]
 *
 * FT-331 (2026-07-07): Belt-and-braces guard against historical ghost-doc poisoning.
 * Root cause of FT-324 was wrapper items (e.g. `{id: '<uuid>_checklist',
 * data: {_sourceModule: 'checklist', pre_departure: {items:[...]}}}`) leaking into
 * the else-branch — they have `.id` and `data._isArray` is falsy, so they got
 * pushed as if they were real completion records and poisoned the canonical doc.
 * Prod + staging cloud DBs were cleaned, but if extractModule ever misfires or a
 * new ghost pattern emerges, the same class of poisoning can recur. This guard
 * rejects any item that carries the canonical `_sourceModule` wrapper marker
 * without the fields a real completion record would have (`checklistType`, `type`,
 * `completedAt`, `checks`).
 *
 * FT-344 (2026-07-08): Photo records added to the whitelist via `capturedAt`.
 * Photo records enqueued via the outbox path can carry `_sourceModule` in
 * `data` (a natural artifact of the sync-bridge wrapping) AND legitimately
 * discriminate themselves as photos with a `capturedAt` timestamp. Without
 * whitelisting `capturedAt`, real photo records would be dropped as wrappers.
 * See DESIGN-BRIEF §3.4 for the full ghost-doc / photo interaction.
 *
 * FT-331 amend (2026-07-07): Wrapper rejection now emits a `devWarn` (was silent
 * drop) so false positives surface in dev/staging telemetry instead of hiding.
 *
 * NOTE ON CIRCULAR IMPORTS: This file was created specifically to sidestep the
 * `UniversalSyncBridge → outbox → UniversalSyncBridge` cycle that would have
 * existed if outbox.js had imported extractItems directly from
 * UniversalSyncBridge.js (which itself imports pushOutbox/processOutbox from
 * outbox). Keep this file dependency-free apart from devLog utilities.
 *
 * @param {Array<Object>} records raw pull result (may be nested wrappers)
 * @returns {Array<Object>} flat list of real item records
 */

import { devWarn } from '../utils/devLog';

export const extractItems = (records) => {
  const items = [];
  if (!Array.isArray(records)) return items;

  records.forEach(record => {
    // Case 1: Wrapped data with items array — RECURSE
    if (record?.data?.items && record.data._isArray) {
      items.push(...extractItems(record.data.items));
      return;
    }

    // Not an item at all — skip
    if (!record?.id || record?.data?._isArray) {
      return;
    }

    // FT-331: reject wrapper-shaped items (historical ghost-doc poison shape).
    // A wrapper has data._sourceModule set (canonical marker) but lacks any
    // completion-record fields.
    // FT-331 amend: was a silent drop — now emits devWarn so false positives
    // surface in dev/staging telemetry.
    // FT-344: `capturedAt` added to whitelist — photo records are legitimate
    // records with `_sourceModule` set by the outbox path.
    const isWrapper =
      record?.data?._sourceModule
      && !record?.checklistType
      && !record?.type
      && !record?.completedAt
      && !record?.checks
      && !record?.capturedAt;
    if (isWrapper) {
      devWarn('[FT-331] extractItems dropped wrapper-shaped item', {
        id: record.id,
        sourceModule: record.data?._sourceModule,
      });
      return;
    }

    // Case 2: Actual item — push as-is
    items.push(record);
  });

  return items;
};

export default extractItems;
